/** @jsxImportSource @emotion/react */
import * as Sentry from '@sentry/react';
import { initSentry } from "./sentry";
initSentry();
import { installCfImageRetryHandler } from "./lib/cfImageRetry";
installCfImageRetryHandler();
import { setupNativeApp } from "./lib/nativeSetup";
setupNativeApp();
import { StrictMode, Suspense, lazy } from "react";
import type React from "react";
import { createRoot } from "react-dom/client";

import { createBrowserRouter, Navigate, Outlet, RouterProvider, ScrollRestoration } from "react-router-dom";
import SkipLink from "./components/SkipLink/SkipLink";
import ErrorPage from "./error-page";
import { importWithRetry } from "./components/LazyLoadErrorBoundary/importWithRetry";
const lazyWithRetry = <T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
) => lazy(() => importWithRetry(importer));
const News = lazyWithRetry(() => import("./pages/news/NewsPage"));
const GameHistoryPage = lazyWithRetry(() => import("./pages/GameHistoryPage"));
const StandingsPage = lazyWithRetry(() => import("./components/Standings/Standings.tsx"));
const FinalResultsPage = lazyWithRetry(() => import("./components/Standings/FinalResults.tsx"));
const Nations = lazyWithRetry(() => import("./pages/nations/NationsPage"));
const TradePage = lazyWithRetry(() => import("./pages/Trade"));
const DevelopmentPage = lazyWithRetry(() => import("./pages/Development"));
const Diplomacy = lazyWithRetry(() => import("./pages/Diplomacy"));
import { Box, CssBaseline } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";
const ArticlePage = lazyWithRetry(() => import("./pages/news/ArticlePage"));
const NationPage = lazyWithRetry(() => import("./pages/nations/NationPage"));
const ProfilePage = lazyWithRetry(() => import("./pages/profile/ProfilePage"));
import UserContext from "./components/UserContext/UserContext";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import AdminRoute from "./components/AdminRoute/AdminRoute";
import GuestRoute from "./components/GuestRoute/GuestRoute";
import { SnackbarProvider } from "notistack";
import theme from "./theme";
import { ThemeProvider } from "@mui/material/styles";
const Contact = lazyWithRetry(() => import("./pages/Contact"));
// Login pages
const LoginChooserPage = lazyWithRetry(() => import("./pages/LoginChooserPage"));
const EmailSignInPage = lazyWithRetry(() => import("./pages/EmailSignInPage"));
const GoogleSignInPage = lazyWithRetry(() => import("./pages/GoogleSignInPage"));
const ResetPage = lazyWithRetry(() => import("./pages/Reset"));
const ConfirmPage = lazyWithRetry(() => import("./pages/ConfirmPage"));
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "./lib/queryClient";
import { PermissionProvider } from "./services/permissionService";
import NewHeader from "./components/NewHeader/NewHeader.tsx";
import { css } from "@emotion/react";
const Page = lazyWithRetry(() => import("./pages/Page"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
import Footer from "./components/Footer/Footer";
const HelpPage = lazyWithRetry(() => import("./pages/HelpPage"));
const GettingStartedPage = lazyWithRetry(() => import("./pages/GettingStarted"));
const TenDaysExamplePage = lazyWithRetry(() => import("./components/GettingStarted/TenDaysExample"));
import Analytics from "./components/Analytics/Analytics";
const Suggestions = lazyWithRetry(() => import("./components/Suggestions/Suggestions"));
import { GoogleOAuthProvider } from "@react-oauth/google";
const InvitePage = lazyWithRetry(() => import("./pages/InvitePage"));
const ConstitutionPage = lazyWithRetry(() => import("./pages/nations/ConstitutionPage"));
const Home = lazyWithRetry(() => import("./pages/Home"));
const DemoSignUpPage = lazyWithRetry(() => import("./pages/DemoSignUpPage"));
const StudentSignUpPage = lazyWithRetry(() => import("./pages/StudentSignUpPage"));
import VersionChecker from "./components/VersionChecker/VersionChecker";
import { ImportErrorComponent } from "./components/LazyLoadErrorBoundary/LazyLoadErrorBoundary";
import ImpersonationBanner from "./components/ImpersonationBanner/ImpersonationBanner";
import ShowcaseBanner from "./components/Showcase/ShowcaseBanner";
const Controls = lazyWithRetry(() => import("./components/Controls/Controls"));
const AchievementsPage = lazyWithRetry(() => import("./pages/AchievementsPage"));
const BadgesPage = lazyWithRetry(() => import("./pages/BadgesPage"));
const UpdatesPage = lazyWithRetry(() => import("./pages/UpdatesPage"));
import VerifyEmailPage from "./pages/VerifyEmailPage.tsx";
import LoadingScreen from "./components/Loading/LoadingScreen";
import GameOverBanner from "./components/GameOverBanner/GameOverBanner";
const NoClassroomPage = lazyWithRetry(() => import("./pages/NoClassroomPage"));
const root = createRoot(
  document.getElementById("root") as HTMLElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  }
);

const router = createBrowserRouter([
  {
    errorElement: <ErrorPage />,
    element: (
      <StrictMode>
        <HelmetProvider>
          <Analytics>
            <ThemeProvider theme={theme}>
              <QueryClientProvider client={queryClient}>
                <CssBaseline />
                <SnackbarProvider>
                  <UserContext>
                    <>
                      <CssBaseline />
                      <ScrollRestoration />
                      <Suspense fallback={<div />}>
                        <Outlet />
                      </Suspense>
                    </>
                  </UserContext>
                </SnackbarProvider>
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </ThemeProvider>
          </Analytics>
        </HelmetProvider>
      </StrictMode>
    ),
    children: [
      {
        path: "/app",
        element: (
          <>
            <ProtectedRoute>
              <PermissionProvider>
                <VersionChecker />
                <SkipLink />
                <NewHeader />
                <ImpersonationBanner />
                <ShowcaseBanner />
                <GameOverBanner />
                <main
                  id={"main"}
                  css={css`
                    padding-left: 5px;
                    padding-right: 5px;
                    padding-bottom: 20px;
                    padding-top: 20px;
                  `}
                >
                  {/* Delayed so a page that loads quickly doesn't flash a spinner. */}
                  <Suspense fallback={<LoadingScreen fullScreen delayMs={250} />}>
                    <Outlet />
                  </Suspense>
                </main>
              </PermissionProvider>
            </ProtectedRoute>
          </>
        ),
        children: [
          { index: true, element: <Navigate to="/app/news" replace /> },
          {
            path: "/app/news",
            element: (
              <Page title={"News"} heading="hidden">
                <News />
              </Page>
            ),
          },
          {
            path: "/app/news/page/:page",
            element: (
              <Page title={"News"} heading="hidden">
                <News />
              </Page>
            ),
          },
          {
            path: "/app/news/:id",
            element: (
              <Page title={"Article"}>
                <ArticlePage />
              </Page>
            ),
          },
          {
            path: "/app/history",
            element: (
              <Page title={"Game History"}>
                <GameHistoryPage />
              </Page>
            ),
          },
          {
            path: "/app/history/page/:page",
            element: (
              <Page title={"Game History"}>
                <GameHistoryPage />
              </Page>
            ),
          },

          {
            path: "/app/standings",
            element: (
              <Page title={"Standings"}>
                <StandingsPage />
              </Page>
            ),
          },
          {
            path: "/app/standings/final",
            element: (
              <Page title={"Final Results"}>
                <FinalResultsPage />
              </Page>
            ),
          },
          {
            path: "/app/nations",
            element: (
              <Page title={"Nations"} heading="visible">
                <Nations />
              </Page>
            ),
          },

          {
            path: "/app/nations/:id",
            element: (
              <Page title={"Nation"} heading="hidden">
                <NationPage />
              </Page>
            ),
          },

          {
            path: "/app/nations/:id/constitution",
            element: (
              <Page title={"Constitution"}>
                <ConstitutionPage />
              </Page>
            ),
          },

          {
            path: "/app/trade",
            element: (
              <Page title={"Trade"} heading="visible">
                <TradePage />
              </Page>
            ),
          },

          {
            path: "/app/map",
            hydrateFallbackElement: <></>,
            async lazy() {
              try {
                /* @vite-ignore */
                const { MapPage } = await importWithRetry(
                  () => import("./pages/MapPage"),
                );
                return {
                  Component: () => {
                    return (
                      <Page title={"Map"} heading="hidden">
                        <MapPage />
                      </Page>
                    );
                  },
                };
              } catch {
                return {
                  Component: () => <ImportErrorComponent />,
                };
              }
            },
          },

          {
            path: "/app/development",
            element: (
              <Page title={"Development"} heading="visible">
                <DevelopmentPage />
              </Page>
            ),
          },

          {
            path: "/app/diplomacy",
            element: (
              <Page title={"Diplomacy"} heading="visible">
                <Diplomacy />
              </Page>
            ),
          },

          {
            path: "/app/profile",
            // "0" is the "current user" id used everywhere else in the UI.
            element: <Navigate to="/app/profile/0" replace />,
          },
          {
            path: "/app/guide",
            element: (
              <Page title={"Guide"}>
                <HelpPage />
              </Page>
            ),
          },
          {
            path: "/app/getting_started",
            element: (
              <AdminRoute>
                <Page title={"Getting Started"}>
                  <GettingStartedPage />
                </Page>
              </AdminRoute>
            ),
          },
          {
            path: "/app/getting-started/ten-days-example",
            element: (
              <AdminRoute>
                <Page title={"10 Days in GeoStrat"}>
                  <TenDaysExamplePage />
                </Page>
              </AdminRoute>
            ),
          },
          {
            path: "/app/profile/:id",
            element: (
              <Page title={"Profile"} heading="hidden">
                <ProfilePage />
              </Page>
            ),
          },

          {
            path: "/app/suggestions",
            element: (
              <Page title={"Suggestions"} heading="visible">
                <Suggestions />
              </Page>
            ),
          },

          {
            path: "/app/updates",
            element: (
              <Page title={"Updates"}>
                <UpdatesPage />
              </Page>
            ),
          },
          {
            path: "/app/controls",
            element: (
              <AdminRoute>
                <Page title={"Controls"} heading="hidden">
                  <Controls />
                </Page>
              </AdminRoute>
            ),
          },
          {
            path: "/app/achievements",
            element: (
              <Page title={"Achievements"}>
                <AchievementsPage />
              </Page>
            ),
          },
          {
            path: "/app/badges",
            element: (
              <AdminRoute>
                <Page title={"Commendations"}>
                  <BadgesPage />
                </Page>
              </AdminRoute>
            ),
          },
        ],
      },

      {
        path: "/",
        element: (
          <Page
            title={"GeoStrat — Strategy-Based Geography Game for Classrooms"}
            description={
              "GeoStrat is an interactive, strategy-based geography game for classrooms. Build nations, trade, form alliances, and teach real-world decision-making."
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Home />
              </Box>
              <Footer />
            </Box>
          </Page>
        ),
        children: [
          {
            path: "/login",
            element: (
              <GuestRoute>
                <Page
                  title={"Login — GeoStrat"}
                  description={
                    "Log in to GeoStrat to manage your classes and gameplay."
                  }
                  noIndex
                >
                  <GoogleOAuthProvider clientId="858808532426-85clcmvdjil1kges81eaa7udk4sogqlf.apps.googleusercontent.com">
                    <LoginChooserPage />
                  </GoogleOAuthProvider>
                </Page>
              </GuestRoute>
            ),
          },
          {
            path: "/login/email",
            element: (
              <GuestRoute>
                <Page
                  title={"Email Login — GeoStrat"}
                  description={"Access your GeoStrat account via email login."}
                  noIndex
                >
                  <GoogleOAuthProvider clientId="858808532426-85clcmvdjil1kges81eaa7udk4sogqlf.apps.googleusercontent.com">
                    <EmailSignInPage />
                  </GoogleOAuthProvider>
                </Page>
              </GuestRoute>
            ),
          },
          {
            path: "/login/google",
            element: (
              <GuestRoute>
                <Page
                  title={"Google Login — GeoStrat"}
                  description={"Sign in to GeoStrat with Google."}
                  noIndex
                >
                  <GoogleOAuthProvider clientId="858808532426-85clcmvdjil1kges81eaa7udk4sogqlf.apps.googleusercontent.com">
                    <GoogleSignInPage />
                  </GoogleOAuthProvider>
                </Page>
              </GuestRoute>
            ),
          },
          {
            path: "/verify_email",
            element: (
              <GuestRoute>
                <Page title={"Verify Email — GeoStrat"} noIndex>
                  <VerifyEmailPage />
                </Page>
              </GuestRoute>
            ),
          },
          {
            path: "/invite/:code",
            element: (
              <GuestRoute>
                <Page title={"Invite — GeoStrat"} noIndex>
                  <GoogleOAuthProvider clientId="858808532426-85clcmvdjil1kges81eaa7udk4sogqlf.apps.googleusercontent.com">
                    <InvitePage />
                  </GoogleOAuthProvider>
                </Page>
              </GuestRoute>
            ),
          },
          {
            path: "/demo",
            element: (
              <GuestRoute>
                <Page
                  title={"GeoStrat Demo — Get Started"}
                  description={
                    "Try GeoStrat with a quick demo setup designed for teachers and classrooms."
                  }
                  noIndex
                >
                  <DemoSignUpPage />
                </Page>
              </GuestRoute>
            ),
          },
          {
            path: "/join-class",
            element: (
              <GuestRoute>
                <Page
                  title={"Join Class — GeoStrat"}
                  description={"Join your GeoStrat class as a student."}
                  noIndex
                >
                  <GoogleOAuthProvider clientId="858808532426-85clcmvdjil1kges81eaa7udk4sogqlf.apps.googleusercontent.com">
                    <StudentSignUpPage />
                  </GoogleOAuthProvider>
                </Page>
              </GuestRoute>
            ),
          },
          {
            path: "/join-game",
            element: (
              <GuestRoute>
                <Page
                  title={"Join Game — GeoStrat"}
                  description={"Join your GeoStrat game as a student."}
                  noIndex
                >
                  <GoogleOAuthProvider clientId="858808532426-85clcmvdjil1kges81eaa7udk4sogqlf.apps.googleusercontent.com">
                    <StudentSignUpPage />
                  </GoogleOAuthProvider>
                </Page>
              </GuestRoute>
            ),
          },
          {
            path: "/reset",
            element: (
              <GuestRoute>
                <Page title={"Reset Password — GeoStrat"} noIndex>
                  <ResetPage />
                </Page>
              </GuestRoute>
            ),
          },
          {
            path: "/confirm/:token",
            element: (
              <GuestRoute>
                <Page title={"Confirm Email — GeoStrat"} noIndex>
                  <ConfirmPage />
                </Page>
              </GuestRoute>
            ),
          },
          {
            path: "/privacy-policy",
            element: (
              <Page
                title={"GeoStrat Privacy Policy"}
                description={
                  "Read GeoStrat’s privacy policy to learn how we handle data and protect user privacy."
                }
              >
                <Privacy />
              </Page>
            ),
          },
          {
            path: "/terms-of-service",
            element: (
              <Page
                title={"GeoStrat Terms of Service"}
                description={
                  "Review the terms and conditions for using GeoStrat in your classroom."
                }
              >
                <Terms />
              </Page>
            ),
          },
          {
            path: "/contact",
            element: (
              <Page
                title={"Contact GeoStrat"}
                description={
                  "Get in touch with the GeoStrat team for questions, support, or feedback."
                }
              >
                <Contact />
              </Page>
            ),
          },
        ],
      },
      {
        path: "/home",
        element: (
          <Page
            title={"GeoStrat - Strategy-Based Geography Game for Classrooms"}
            description={
              "GeoStrat is an interactive, strategy-based geography game for classrooms. Build nations, trade, form alliances, and teach real-world decision-making."
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Home />
              </Box>
              <Footer />
            </Box>
          </Page>
        ),
      },
      {
        path: "/no-classroom",
        element: (
          <Page title={"No Classroom — GeoStrat"} noIndex>
            <NoClassroomPage />
          </Page>
        ),
      },
      {
        path: "admin/*",
        hydrateFallbackElement: <></>,
        async lazy() {
          try {
            const { AdminDashboardPage } = await importWithRetry(
              () => import("./pages/AdminDashboard"),
            );
            return {
              Component: () => (
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              ),
            };
          } catch {
            return {
              Component: () => <ImportErrorComponent />,
            };
          }
        },
      },
    ],
  },
]);

// Render immediately without waiting for Sentry
root.render(<RouterProvider router={router} />);
